import { driveList, type DriveProposal } from './drives'
import { computeDynamics, impulseBalance, type Balance, type DynamicsResult } from './dynamics'
import { computePower, type PowerResult } from './power'
import { generateGait, type GaitResult } from './gait'
import { computeGround, totalMass, type GroundResult } from './ground'
import { massCheck, planeLimits, type HumanoidModel, type LimitSet, type MassCheck } from './model'
import { buildSkeleton } from './kinematics'
import { gaitFor, referenceSpeed, stanceFraction, strideLength } from './reference'
import { toeResult, type ToeResult } from './toe'
import type { SimState } from './state'

/**
 * Ein Durchlauf des Rechenkerns — die eine Funktion, die die Seiten aufrufen.
 *
 * Sie bindet die Kette zusammen und macht sonst nichts. Jede Seite bekommt
 * dasselbe Ergebnis; keine Seite rechnet selbst.
 *
 * DIE REIHENFOLGE IST DIE AUSSAGE:
 *
 *   Zustand → Gang → Bodenkontakt → Dynamik → Antriebe
 *
 * Eine Richtung, keine Rückkanäle. Wer den Zustand ändert, bekommt ein neues
 * Ergebnis; wer das Ergebnis ändern will, muss den Zustand ändern. Deshalb gibt
 * es hier auch keine Setter: das Ergebnis ist ein Rückgabewert, kein Objekt mit
 * Methoden.
 */
export interface Simulation {
  model: HumanoidModel
  state: SimState
  gait: GaitResult
  ground: GroundResult
  dynamics: DynamicsResult
  drives: DriveProposal[]
  /** Was der Antriebsstrang daraus macht — Speicher, Rückspeisung, Halteverluste. */
  power: PowerResult
  /**
   * Das Zehengrundgelenk — was der Gang von ihm verlangt und was die Feder
   * daraus macht.
   *
   * Es steht neben den Antrieben und nicht in ihnen: das Gelenk ist PASSIV und
   * erzeugt keine Antriebszeile. Wer es dort suchte, fände nichts und schlösse
   * daraus, es sei kräftefrei.
   */
  toe: ToeResult
  /** Zusammenfassung für die Kopfzeilen — alles abgeleitet, nichts gespeichert. */
  metrics: Metrics
  limits: LimitSet
  mass: MassCheck
  balance: Balance
}

export interface Metrics {
  /** Fortbewegungsgeschwindigkeit, m/s. */
  speed: number
  /** Doppelschrittlänge, m. */
  stride: number
  /** Zyklusdauer, s. */
  cycleTime: number
  /** Kadenz, Schritte je Minute. */
  cadence: number
  /** Standphasenanteil, 0 … 1. */
  stance: number
  /** Flugzeit je Schritt, s. */
  flight: number
  /** Gesamtmasse einschliesslich Zuladung, kg. */
  mass: number
  /** Grösste senkrechte Bodenreaktionskraft, Vielfaches des Körpergewichts. */
  groundPeak: number
  /** Tiefste Durchdringung des Bodens, m. */
  groundError: number
  /** Grösstes Schweben eines belasteten Fusses, m. */
  groundFloat: number
  /** Wie oft die Kinematik an ihre Grenze stiess. */
  clipped: number
  /** Grösste Ausnutzung eines zulässigen Gelenkmoments. */
  worstMomentUse: number
  /** Achsen, für die kein Katalogantrieb passt. */
  infeasible: number
  /** Mittlere Leistung am Akku, W — einschliesslich Halte- und Grundlast. */
  netPower: number
  /** Anteil der Antriebsarbeit aus den Speichern, 0 … 1. */
  recovered: number
  /** Transportkosten: Energie je Kilogramm und Meter, auf g bezogen. */
  transportCost: number
}

/** Erdbeschleunigung, m/s² — für die Umrechnung auf Körpergewichte. */
const G = 9.81

export function runSimulation(model: HumanoidModel, state: SimState): Simulation {
  const gait = gaitFor(model, state, generateGait)
  const ground = computeGround(model, state, gait)
  const dynamics = computeDynamics(model, state, gait, ground)
  const drives = driveList(model, gait, dynamics, state.off)
  const balance = impulseBalance(dynamics)
  const toe = toeResult(
    buildSkeleton(model),
    dynamics.poses,
    dynamics.grf,
    dynamics.cop,
    dynamics.samples,
    state.features.toeSpring,
  )

  let peak = 0
  for (let i = 0; i < dynamics.samples; i++) {
    peak = Math.max(peak, dynamics.grf.R[i].y + dynamics.grf.L[i].y)
  }

  let worst = 0
  let infeasible = 0
  for (const proposal of drives) {
    if (proposal.demand.momentUse !== null) {
      worst = Math.max(worst, proposal.demand.momentUse)
    }
    if (!proposal.feasible) infeasible++
  }

  const speed = state.generated ? gait.report.speed : referenceSpeed(model, state)
  const stride = state.generated ? 2 * state.task.step : strideLength(model, state)
  const mass = totalMass(model, state)
  const power = computePower(
    state,
    dynamics,
    drives.map((d) => d.demand),
    mass,
    stride,
  )

  return {
    model,
    state,
    gait,
    ground,
    dynamics,
    drives,
    power,
    toe,
    limits: planeLimits(model, Object.keys(state.off)),
    mass: massCheck(model),
    balance,
    metrics: {
      speed,
      stride,
      cycleTime: gait.cycleTime,
      cadence: state.trim.cadence,
      stance: stanceFraction(state),
      flight: gait.report.flight,
      mass,
      groundPeak: peak / (dynamics.mass * G),
      groundError: ground.penetration,
      groundFloat: ground.float,
      clipped: gait.report.clipHigh + gait.report.clipLow,
      worstMomentUse: worst,
      infeasible,
      netPower: power.meanPower,
      recovered: power.recoveredShare,
      transportCost: power.transportCost,
    },
  }
}
